import React, { useState, useRef, useEffect } from 'react';
import { Field } from 'react-final-form';
import css from './SearchCTA.module.css';

const MIN_GUESTS = 0;
const MAX_GUESTS = 999;

const FilterGuestCount = () => {
  const [open, setOpen] = useState(false);
  const containerRef = useRef(null);

  // Close popup on outside click
  useEffect(() => {
    if (!open) return;
    const handleClick = e => {
      if (containerRef.current && !containerRef.current.contains(e.target)) {
        setOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClick);
    return () => document.removeEventListener('mousedown', handleClick);
  }, [open]);

  return (
    <div
      className={css.filterField}
      ref={containerRef}
      style={{
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'stretch', // Match other categories (usually stretch, not center)
        justifyContent: 'center',
        minWidth: 0,
        width: '100%',
        position: 'relative',
        padding: 0, // Remove extra padding if present
        boxSizing: 'border-box',
      }}
    >
      <Field name="guestCount" parse={v => Math.max(MIN_GUESTS, Math.min(MAX_GUESTS, Number(v) || MIN_GUESTS))}>
        {({ input }) => {
          const value = Number(input.value) || MIN_GUESTS;
          return (
            <>
              <button
                type="button"
                aria-label="Select guest count"
                onClick={() => setOpen(v => !v)}
                className={css.categoryButton}
                style={{
                  width: '100%',
                  minWidth: 0,
                  padding: '8px 16px',
                  border: 'none',
                  borderRadius: 8,
                  background: '#fff',
                  fontSize: 'inherit', // Use inherit to match parent font
                  fontFamily: 'inherit', // Ensure font family matches
                  fontWeight: 500,
                  color: '#333',
                  textAlign: 'left',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'space-between',
                  cursor: 'pointer',
                  outline: open ? '2px solid #0077ff' : 'none',
                  boxShadow: open ? '0 2px 8px rgba(0,0,0,0.08)' : 'none',
                  transition: 'outline 0.2s, box-shadow 0.2s',
                  height: 40,
                }}
              >
                <span>
                  <span style={{ fontWeight: 600 }}>{value}</span> guests
                </span>
                <span style={{ marginLeft: 8, fontSize: 16, color: '#888' }}>{open ? '\u25B2' : '\u25BC'}</span>
              </button>
              {open && (
                <div
                  className={css.categoryPopup} // Use same popup class as Category/Dates
                  style={{
                    position: 'absolute',
                    top: '110%',
                    left: 0,
                    right: 0,
                    zIndex: 10,
                    background: '#fff',
                    border: '1px solid #ddd',
                    borderRadius: 8,
                    boxShadow: '0 4px 16px rgba(0,0,0,0.10)',
                    padding: 16,
                    display: 'flex',
                    flexDirection: 'column',
                    alignItems: 'center',
                  }}
                >
                  <label
                    htmlFor="guestCount"
                    style={{
                      fontWeight: 500,
                      marginBottom: 4,
                      fontSize: 14,
                      color: 'var(--colorGrey700)',
                      fontFamily: 'inherit',
                    }}
                  >
                    Guest Count
                  </label>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 6, marginBottom: 8 }}>
                    <button
                      type="button"
                      aria-label="Decrease guests"
                      onMouseDown={e => {
                        e.preventDefault();
                        let interval;
                        const step = () => {
                          input.onChange(Math.max(MIN_GUESTS, Number(input.value) - 1));
                        };
                        step();
                        interval = setInterval(step, 100);
                        const clear = () => {
                          clearInterval(interval);
                          document.removeEventListener('mouseup', clear);
                          document.removeEventListener('mouseleave', clear);
                        };
                        document.addEventListener('mouseup', clear);
                        document.addEventListener('mouseleave', clear);
                      }}
                      onMouseUp={e => e.preventDefault()}
                      onMouseLeave={e => e.preventDefault()}
                      onClick={e => {
                        e.preventDefault();
                        input.onChange(Math.max(MIN_GUESTS, Number(input.value) - 1));
                      }}
                      disabled={value <= MIN_GUESTS}
                      style={{
                        width: 28,
                        height: 28,
                        border: '1px solid #ccc',
                        borderRadius: 4,
                        background: value <= MIN_GUESTS ? '#f5f5f5' : '#fff',
                        color: value <= MIN_GUESTS ? '#bbb' : '#333',
                        fontSize: 18,
                        fontWeight: 600,
                        cursor: value <= MIN_GUESTS ? 'not-allowed' : 'pointer',
                        transition: 'border 0.2s',
                        outline: 'none',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        marginRight: 2,
                        userSelect: 'none',
                      }}
                      tabIndex={0}
                    >
                      −
                    </button>
                    <input
                      type="text"
                      value={value}
                      onChange={e => {
                        let v = e.target.value.replace(/[^0-9]/g, '');
                        if (v === '') v = MIN_GUESTS;
                        let num = Math.max(MIN_GUESTS, Math.min(MAX_GUESTS, Number(v)));
                        input.onChange(num);
                      }}
                      style={{
                        width: '64px',
                        padding: '6px 8px',
                        border: '1px solid #ccc',
                        borderRadius: '4px',
                        fontSize: 'inherit', // Match font size
                        fontFamily: 'inherit', // Match font family
                        fontWeight: 500,
                        outline: 'none',
                        background: '#fff',
                        boxShadow: 'none',
                        transition: 'border 0.2s',
                        textAlign: 'center',
                        margin: '0 2px',
                        display: 'inline-block',
                      }}
                      aria-label="Guest Count"
                      inputMode="numeric"
                      pattern="[0-9]*"
                      min={MIN_GUESTS}
                      max={MAX_GUESTS}
                    />
                    <button
                      type="button"
                      aria-label="Increase guests"
                      onMouseDown={e => {
                        e.preventDefault();
                        let interval;
                        const step = () => {
                          input.onChange(Math.min(MAX_GUESTS, Number(input.value) + 1));
                        };
                        step();
                        interval = setInterval(step, 100);
                        const clear = () => {
                          clearInterval(interval);
                          document.removeEventListener('mouseup', clear);
                          document.removeEventListener('mouseleave', clear);
                        };
                        document.addEventListener('mouseup', clear);
                        document.addEventListener('mouseleave', clear);
                      }}
                      onMouseUp={e => e.preventDefault()}
                      onMouseLeave={e => e.preventDefault()}
                      onClick={e => {
                        e.preventDefault();
                        input.onChange(Math.min(MAX_GUESTS, Number(input.value) + 1));
                      }}
                      disabled={value >= MAX_GUESTS}
                      style={{
                        width: 28,
                        height: 28,
                        border: '1px solid #ccc',
                        borderRadius: 4,
                        background: value >= MAX_GUESTS ? '#f5f5f5' : '#fff',
                        color: value >= MAX_GUESTS ? '#bbb' : '#333',
                        fontSize: 18,
                        fontWeight: 600,
                        cursor: value >= MAX_GUESTS ? 'not-allowed' : 'pointer',
                        transition: 'border 0.2s',
                        outline: 'none',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        marginLeft: 2,
                        userSelect: 'none',
                      }}
                      tabIndex={0}
                    >
                      +
                    </button>
                    <span style={{ fontSize: 13, color: '#888', marginLeft: 8 }}>guests</span>
                  </div>
                  {/* Show provider-specific helper text only if in the edit listing wizard */}
                  {typeof window !== 'undefined' && window.location.pathname.match(/edit(listing|[-_]listing)/i) && (
                    <span style={{ fontSize: 11, color: '#aaa', marginTop: 2 }}>
                      Most people book multiple bartenders, chefs and food trucks for parties higher than 50
                    </span>
                  )}
                  <button
                    type="button"
                    onClick={() => setOpen(false)}
                    style={{
                      marginTop: 10,
                      background: '#f5f5f5',
                      border: 'none',
                      borderRadius: 4,
                      padding: '6px 16px',
                      fontSize: 13,
                      color: '#333',
                      cursor: 'pointer',
                    }}
                  >
                    Done
                  </button>
                </div>
              )}
            </>
          );
        }}
      </Field>
    </div>
  );
};

export default FilterGuestCount;
